import { v4 as uuidv4 } from 'uuid';

import i18n from '../../i18n';
import {
  DEFAULT_ELEMENT_STYLE,
  DEFAULT_FILTERS,
  DEFAULT_SHAPE,
  DEFAULT_TEXT_BACKGROUND,
  DEFAULT_TEXT_OUTLINE,
  DEFAULT_TEXT_SHADOW,
  TOOLS,
} from '../config';
import { layoutText } from '../elements/text/textLayout';
import { BLOCK_STYLE_DEFAULTS, normalizeRuns, runsToText, textToRuns } from '../elements/text/textRuns';

const getDefaultText = () => i18n.t('textPlaceholder', { ns: 'mediaStudio' });

export const DEFAULT_LINE_HEIGHT = 1.2;

export const DEFAULT_LETTER_SPACING = 0;

export const ELEMENT_TYPES = {
  IMAGE: 'image',
  TEXT: 'text',
  SHAPE: 'shape',
};

export const createEmptyDocument = ({ width = 1080, height = 1080, backgroundColor = '#ffffff' } = {}) => ({
  width,
  height,
  background: { type: 'color', color: backgroundColor },
  elements: [],
  selectedId: null,
  selectedIds: [],
  tool: TOOLS.SELECT,
  cropRatio: 'original',
  cropRect: null,
  activeFormatId: null,
  outputSize: null,
  pendingOutputSize: null,
  viewport: { zoom: 1, x: 0, y: 0 },
  ui: {
    gridEnabled: false,
    snapToObjects: true,
    keepAspectRatioDefault: true,
  },
  hasDocument: true,
});

export const createInitialState = () => ({
  width: 0,
  height: 0,
  background: { type: 'color', color: '#ffffff' },
  elements: [],
  selectedId: null,
  selectedIds: [],
  tool: TOOLS.SELECT,
  cropRatio: 'original',
  cropRect: null,
  activeFormatId: null,
  outputSize: null,
  pendingOutputSize: null,
  viewport: { zoom: 1, x: 0, y: 0 },
  ui: {
    gridEnabled: false,
    snapToObjects: true,
    keepAspectRatioDefault: true,
  },
  hasDocument: false,
});

export const createImageElement = ({ image, objectUrl, naturalWidth, naturalHeight, name }, overrides = {}) => {
  const width = overrides.width ?? naturalWidth;
  const height = overrides.height ?? naturalHeight;

  return {
    id: uuidv4(),
    name: name || 'Image',
    image,
    objectUrl,
    src: {
      objectUrl,
      naturalWidth,
      naturalHeight,
    },
    naturalWidth,
    naturalHeight,
    x: overrides.x ?? width / 2,
    y: overrides.y ?? height / 2,
    width,
    height,
    ...DEFAULT_ELEMENT_STYLE,
    crop: null,
    visible: true,
    locked: false,
    ...overrides,
    filters: { ...DEFAULT_FILTERS, ...(overrides.filters || {}) },
    type: ELEMENT_TYPES.IMAGE,
  };
};

export const getTextDisplayName = (element) => {
  const raw = (element?.text || element?.name || 'Text').replace(/\s+/g, ' ').trim();
  if (raw.length <= 28) {
    return raw || 'Text';
  }
  return `${raw.slice(0, 28)}…`;
};

export const createTextElement = (overrides = {}) => {
  const { shadow, outline, background, runs: runsOverride, text: textOverride, ...rest } = overrides;
  const runs = normalizeRuns(runsOverride || textToRuns(textOverride ?? getDefaultText()));
  const text = runsToText(runs);
  const width = rest.width ?? 480;

  const element = {
    lineHeight: DEFAULT_LINE_HEIGHT,
    letterSpacing: DEFAULT_LETTER_SPACING,
    align: 'left',
    opacity: 1,
    blendMode: 'source-over',
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    visible: true,
    locked: false,
    ...BLOCK_STYLE_DEFAULTS,
    ...rest,
    id: uuidv4(),
    type: ELEMENT_TYPES.TEXT,
    name: rest.name || getTextDisplayName({ text }),
    keepAspectRatio: false,
    text,
    runs,
    width,
    shadow: { ...DEFAULT_TEXT_SHADOW, ...(shadow || {}) },
    outline: { ...DEFAULT_TEXT_OUTLINE, ...(outline || {}) },
    background: { ...DEFAULT_TEXT_BACKGROUND, ...(background || {}) },
  };
  element.height = rest.height ?? layoutText(element).height;
  element.x = rest.x ?? width / 2;
  element.y = rest.y ?? element.height / 2;
  return element;
};

export const createShapeElement = (overrides = {}) => ({
  opacity: 1,
  blendMode: 'source-over',
  scaleX: 1,
  scaleY: 1,
  rotation: 0,
  keepAspectRatio: false,
  visible: true,
  locked: false,
  ...DEFAULT_SHAPE,
  ...overrides,
  id: uuidv4(),
  type: ELEMENT_TYPES.SHAPE,
  name: overrides.name || 'Shape',
});

export const cloneElement = (element) => ({
  ...element,
  filters: element.filters ? { ...element.filters } : element.filters,
  crop: element.crop ? { ...element.crop } : element.crop,
  src: element.src ? { ...element.src } : element.src,
  shadow: element.shadow ? { ...element.shadow } : element.shadow,
  outline: element.outline ? { ...element.outline } : element.outline,
  background: element.background ? { ...element.background } : element.background,
  runs: element.runs ? element.runs.map((run) => ({ ...run, style: { ...run.style } })) : element.runs,
});

export const duplicateElement = (element, { dx = 0, dy = 0 } = {}) => ({
  ...cloneElement(element),
  id: uuidv4(),
  x: element.x + dx,
  y: element.y + dy,
});

export const getElementInsertIndex = (elements, element) =>
  element.type === ELEMENT_TYPES.TEXT
    ? elements.length
    : elements.findLastIndex((item) => item.type !== ELEMENT_TYPES.TEXT) + 1;

export const ELEMENT_NODE_PREFIX = 'element-';

export const getElementNodeId = (id) => `${ELEMENT_NODE_PREFIX}${id}`;

export const getElementObjectUrl = (element) => element?.objectUrl || element?.src?.objectUrl;
